<template>
  <vs-card class="card">
    <vs-wrapper>
      <v-skeleton-loader v-if="isManuallyRefetching" type="card-heading" />
      <div v-else class="card-inner">
        <div class="sync-target-icon">
          <SyncTargetImages :logo="logoName" />
        </div>
        <div class="flex-grow-1">
          <vs-text type="intro" bold>{{ target.name }} Sync</vs-text>
          <p style="margin: 0.5em"></p>
          <v-row>
            <v-col cols="10">
              <vs-text>{{ statusText }}</vs-text>
            </v-col>
            <v-col
              v-if="
                syncStatus === BatchSyncStatus.SyncSuccess ||
                syncStatus === BatchSyncStatus.SyncFailure
              "
              cols="2"
            >
              <v-icon
                :color="syncStatus === BatchSyncStatus.SyncFailure ? 'red' : 'var(--color-blue)'"
                >{{
                  syncStatus === BatchSyncStatus.SyncFailure
                    ? 'mdi-cloud-off-outline'
                    : 'mdi-cloud-check-variant-outline'
                }}</v-icon
              >
            </v-col>
          </v-row>
          <vs-text type="warning">{{ syncFailureMessage }}</vs-text>
        </div>
      </div>
      <v-row dense>
        <v-col>
          <vs-button
            full-width
            :label="buttonLabel"
            :type="buttonStyleType"
            :disabled="
              !batchHasLineItems ||
              syncStatus === BatchSyncStatus.AwaitingInitialSync ||
              syncStatus === BatchSyncStatus.UpdatesPending
            "
            @click="handleSyncClicked"
          />
        </v-col>
        <v-col
          v-if="
            syncStatus === BatchSyncStatus.AwaitingInitialSync ||
            syncStatus === BatchSyncStatus.UpdatesPending
          "
          cols="auto"
        >
          <vs-button
            icon="mdi-refresh"
            type="secondary"
            :disabled="isManuallyRefetching"
            @click="manualRefetchSyncTargets"
          />
        </v-col>
      </v-row>
    </vs-wrapper>
  </vs-card>
</template>

<script setup lang="ts">
import SyncTargetImages from '../SyncTargetImages.vue'
import { computed, ref, toRef } from 'vue'
import { formatDateTimeWithYear } from '@/utils/date-formatting'
import { useRouter } from 'vue-router/composables'
import VsWrapper from '@/components/vision/VsWrapper.vue'
import VsText from '@/components/vision/VsText.vue'
import VsButton from '@/components/vision/VsButton.vue'
import VsCard from '@/components/vision/VsCard.vue'
import {
  BatchSyncStatus,
  useBatchSyncStates,
} from '@/components/batches/QuickBooksDesktop/useBatchSyncStates'
import { SyncTarget } from '@/models/batch'
import { useBatchStates } from '@/components/batches/QuickBooksDesktop/useBatchStates'
import { SnackType } from '@/components/vision/SnackBarType'

const props = defineProps<{
  invoiceId: string
  target: SyncTarget
}>()
const router = useRouter()

const batchIdRef = toRef(props, 'invoiceId')
const targetRef = toRef(props, 'target')
const targetIdRef = computed(() => targetRef.value.id)
const { syncTarget, syncFailureMessage, lastSyncedAt, syncStatus, refetchSyncTargets } =
  useBatchSyncStates(batchIdRef, targetIdRef)

const emit = defineEmits<{
  (e: 'sync-requested', value: string)
}>()

const isManuallyRefetching = ref(false)
const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))
const manualRefetchSyncTargets = async () => {
  try {
    isManuallyRefetching.value = true
    await Promise.all([refetchSyncTargets(), sleep(250)])
  } catch (err) {
  } finally {
    isManuallyRefetching.value = false
  }
}

const { batchHasLineItems, batchClientName } = useBatchStates(batchIdRef)

const buttonLabel = computed(() => {
  if (syncStatus.value === BatchSyncStatus.NoSyncTarget) {
    return `Sync to QuickBooks Desktop`
  } else if (
    syncStatus.value === BatchSyncStatus.AwaitingInitialSync ||
    syncStatus.value === BatchSyncStatus.UpdatesPending
  ) {
    return 'Sync pending...'
  } else {
    return 'View details'
  }
})

const buttonStyleType = computed(() => {
  if (
    syncStatus.value === BatchSyncStatus.SyncSuccess ||
    syncStatus.value === BatchSyncStatus.SyncFailure
  ) {
    return 'secondary'
  }
  return 'primary'
})

const statusText = computed(() => {
  if (syncStatus.value === BatchSyncStatus.NoSyncTarget) {
    return 'Not synced yet'
  } else if (syncStatus.value === BatchSyncStatus.AwaitingInitialSync) {
    return 'Queued (waiting on the web connector)'
  } else if (syncStatus.value === BatchSyncStatus.UpdatesPending) {
    return 'Queued (updates detected)'
  } else if (syncStatus.value === BatchSyncStatus.SyncSuccess) {
    return `Synced successfully to ${
      batchClientName.value
    }\nOccurred ${formatDateTimeWithYear(lastSyncedAt.value)}`
  } else if (syncStatus.value === BatchSyncStatus.SyncFailure) {
    return 'Sync failed'
  }
  return ''
})

const logoMap = {
  qbo: 'quickbooks',
  qbd: 'quickbooks',
}

const logoName = computed(() => logoMap[targetRef.value.type.toLowerCase()] || '')

const handleSyncClicked = () => {
  emit('sync-requested', targetIdRef.value)
}
</script>

<style scoped>
.card {
  border: none;
  box-shadow: var(--shadow-low);
}
.card-inner {
  display: flex;
  flex-direction: row;
  gap: var(--space-base);
}

.sync-target-icon {
  width: 30px;
  flex-shrink: 0;
}
</style>
